<template>
	<div class="targetEditorRoot">
		<div v-if="mode === 'visual'">
			<div class="fieldBlock">
				<div class="fieldLabel">Base Metric Path</div>
				<div class="pathRow">
					<input type="text"
						   class="pathInput"
						   v-model="base"
						   :list="datalistId"
						   spellcheck="false"
						   placeholder="stats.gauges.…"
						   aria-label="Base metric path"
						   @input="onBaseInput" />
					<datalist :id="datalistId">
						<option v-for="s in pathSuggestions" :key="s" :value="s"></option>
					</datalist>
					<input type="button" value="Browse…" @click="browseMetrics" />
				</div>
				<div class="fieldHelp">
					The metric (or wildcard pattern) this series is based on.
					<code class="inline">*</code> matches anything within one dot-separated segment,
					e.g. <code class="inline">stats.gauges.WEB*.statref.…</code> selects every matching server.
				</div>
				<div v-if="templateMode && baseHasConcreteServerName" class="templateSuggestion">
					<input type="button" value="Use %%ServerName%% placeholder" @click="substitutePlaceholder" />
					<span class="fieldHelp">This graph is rendered once per server; the placeholder is replaced with each server's name.</span>
				</div>
			</div>

			<div class="fieldBlock">
				<div class="fieldLabel">Functions Applied <span class="labelNote">(top to bottom)</span></div>
				<div v-if="!fns.length" class="fieldHelp">No functions applied — the series is graphed as-is. Functions can rename the legend entry (alias), combine wildcard series (sumSeries), bucket counters into rates (summarize), scale values, and more.</div>
				<div v-for="(fn, fnIndex) in fns" :key="fnIndex" class="fnBox">
					<div class="fnHeader">
						<span class="fnName">{{fn.name}}</span>
						<span v-if="fnDef(fn.name)" class="fnSignature">{{fnDef(fn.name).function}}</span>
						<span class="fnHeaderButtons">
							<input type="button" value="▲" title="Apply this function earlier" :disabled="fnIndex === 0" @click="moveFn(fnIndex, -1)" />
							<input type="button" value="▼" title="Apply this function later" :disabled="fnIndex === fns.length - 1" @click="moveFn(fnIndex, 1)" />
							<input type="button" value="✕" title="Remove this function" @click="removeFn(fnIndex)" />
						</span>
					</div>
					<div v-if="fnSummary(fn.name)" class="fnSummary">{{fnSummary(fn.name)}}</div>
					<div v-for="row in paramRows(fn)" :key="row.index" class="paramRow">
						<label class="paramLabel" :for="paramInputId(fnIndex, row.index)">
							{{row.def ? row.def.name : ("argument " + (row.index + 1))}}<span v-if="row.def && row.def.required" class="requiredMark" title="Required">*</span>
						</label>
						<select v-if="row.choices"
								:id="paramInputId(fnIndex, row.index)"
								:value="row.value"
								@change="setFnArg(fn, row, $event.target.value)">
							<option value="">{{row.def && !row.def.required ? "(default" + (row.defaultText ? ": " + row.defaultText : "") + ")" : "(choose)"}}</option>
							<option v-for="c in row.choices" :key="c" :value="c">{{c}}</option>
						</select>
						<input v-else
							   :id="paramInputId(fnIndex, row.index)"
							   :type="row.numeric ? 'number' : 'text'"
							   step="any"
							   :value="row.value"
							   :placeholder="row.defaultText"
							   :list="row.suggestions ? paramInputId(fnIndex, row.index) + '_list' : null"
							   spellcheck="false"
							   @input="setFnArg(fn, row, $event.target.value)" />
						<datalist v-if="row.suggestions" :id="paramInputId(fnIndex, row.index) + '_list'">
							<option v-for="s in row.suggestions" :key="s" :value="s"></option>
						</datalist>
					</div>
					<details v-if="fnDocs(fn.name)" class="fnDocs">
						<summary>Documentation</summary>
						<pre>{{fnDocs(fn.name)}}</pre>
					</details>
				</div>
				<div class="addFnRow">
					<input type="button" :value="addFnOpen ? 'Close Function List' : '+ Add Function'" @click="toggleAddFn" />
				</div>
				<div v-if="addFnOpen" class="addFnPanel">
					<input ref="addFnSearchInput"
						   type="text"
						   class="addFnSearch"
						   v-model="addFnSearch"
						   placeholder="Filter functions…"
						   aria-label="Filter functions" />
					<div v-if="!functionsMeta" class="fieldHelp">Function metadata is unavailable (the Graphite server could not be reached). You can still edit this series as text.</div>
					<div v-for="group in filteredFnGroups" :key="group.name" class="fnGroup">
						<div class="fnGroupName">{{group.name}}</div>
						<div v-for="def in group.fns" :key="def.name" class="fnChoice">
							<a role="button" tabindex="0" class="fnChoiceName"
							   @click="addFn(def)"
							   @keydown.enter.space.prevent="addFn(def)">{{def.name}}</a>
							<span class="fnChoiceSummary">{{fnSummary(def.name)}}</span>
						</div>
					</div>
				</div>
			</div>
			<div class="modeRow">
				<a role="button" tabindex="0" class="modeLink" @click="switchToRaw" @keydown.enter.space.prevent="switchToRaw">Edit this series as text</a>
			</div>
		</div>

		<div v-else>
			<div class="fieldBlock">
				<div class="fieldLabel">Series Expression</div>
				<textarea class="rawTextarea"
						  v-model="rawText"
						  rows="3"
						  spellcheck="false"
						  aria-label="Series expression"
						  @input="onRawInput"></textarea>
				<div v-if="rawError" class="error">{{rawError}}</div>
				<div class="fieldHelp">A Graphite target expression, e.g. <code class="inline">alias(sumSeries(stats.gauges.WEB*.foo),"Total Foo")</code></div>
			</div>
			<div class="modeRow">
				<a role="button" tabindex="0" class="modeLink" @click="switchToVisual" @keydown.enter.space.prevent="switchToVisual">Edit this series visually</a>
				<span v-if="visualUnavailableReason" class="fieldHelp">{{visualUnavailableReason}}</span>
			</div>
		</div>
	</div>
</template>

<script>
	import { ParseExpression, SerializeExpression, TryBuildChain, BuildAstFromChain, IsLiteralNode } from 'appRoot/scripts/GraphiteQuery';
	import { GraphiteFindMetrics, CleanFunctionDescription } from 'appRoot/api/GraphiteMetadata';
	import { ModalDialog } from 'appRoot/scripts/ModalDialog';
	import GraphiteMetricBrowser from 'appRoot/vues/admin/controls/GraphiteMetricBrowser.vue';

	const NUMERIC_PARAM_TYPES = { integer: true, float: true, node: true, "int": true, intOrInf: true };

	export default {
		props:
		{
			/** Target object { raw, ast, parseError } from the graph model; mutated in place as the user edits. */
			target: {
				type: Object,
				required: true
			},
			/** Function metadata object from Graphite's /functions endpoint, or null while loading/unavailable. */
			functionsMeta: {
				type: Object,
				default: null
			},
			templateMode: {
				type: Boolean,
				default: false
			},
			serverNames: {
				type: Array,
				default: () => []
			}
		},
		data()
		{
			return {
				mode: "raw",
				base: "",
				fns: [],
				rawText: "",
				rawError: null,
				visualUnavailableReason: null,
				addFnOpen: false,
				addFnSearch: "",
				pathSuggestions: [],
				suggestTimeout: null,
				rawParseTimeout: null,
				uid: GetUid()
			};
		},
		created()
		{
			if (!this.target.raw && !this.target.ast)
			{
				// A brand new series starts in the visual editor with an empty base path.
				this.mode = "visual";
				return;
			}
			let chain = this.target.ast ? TryBuildChain(this.target.ast) : null;
			if (chain)
			{
				this.mode = "visual";
				this.base = chain.base;
				this.fns = chain.fns;
			}
			else
			{
				this.mode = "raw";
				this.rawText = this.target.raw || "";
				this.rawError = this.target.parseError;
			}
		},
		beforeUnmount()
		{
			if (this.suggestTimeout)
				clearTimeout(this.suggestTimeout);
			if (this.rawParseTimeout)
				clearTimeout(this.rawParseTimeout);
		},
		computed:
		{
			datalistId()
			{
				return "graphiteMetricSuggest_" + this.uid;
			},
			baseHasConcreteServerName()
			{
				let segments = this.base.split(".");
				return this.serverNames.some(sn => sn && segments.some(seg => seg.toLowerCase() === sn.toLowerCase()));
			},
			/** Chainable functions (first parameter accepts a series) grouped by Graphite's function groups. */
			filteredFnGroups()
			{
				if (!this.functionsMeta)
					return [];
				let search = this.addFnSearch.trim().toLowerCase();
				let groups = {};
				for (let name in this.functionsMeta)
				{
					let def = this.functionsMeta[name];
					if (!def.params || !def.params.length)
						continue;
					let t = def.params[0].type;
					if (t !== "seriesList" && t !== "seriesLists")
						continue;
					if (search && name.toLowerCase().indexOf(search) === -1
						&& (def.description || "").toLowerCase().indexOf(search) === -1)
						continue;
					let groupName = def.group || "Other";
					if (!groups[groupName])
						groups[groupName] = [];
					groups[groupName].push(def);
				}
				let result = [];
				for (let name of Object.keys(groups).sort())
					result.push({ name, fns: groups[name].sort((a, b) => a.name.localeCompare(b.name)) });
				return result;
			}
		},
		methods:
		{
			//////////////////////////////////////////
			// Model synchronization                //
			//////////////////////////////////////////
			/** Rebuilds the target's AST and raw text from the visual chain state. */
			syncOut()
			{
				let ast = BuildAstFromChain(this.base.trim(), this.fns);
				this.target.ast = ast;
				this.target.raw = SerializeExpression(ast);
				this.target.parseError = null;
			},
			//////////////////////////////////////////
			// Base path editing                    //
			//////////////////////////////////////////
			onBaseInput()
			{
				this.syncOut();
				if (this.suggestTimeout)
					clearTimeout(this.suggestTimeout);
				this.suggestTimeout = setTimeout(() => { this.loadPathSuggestions(); }, 350);
			},
			loadPathSuggestions()
			{
				let path = this.base.trim();
				if (!path)
					return;
				if (this.serverNames.length)
					path = path.replace(/%%ServerName%%/gi, this.serverNames[0]);
				GraphiteFindMetrics(path + "*")
					.then(results =>
					{
						this.pathSuggestions = results.slice(0, 50).map(r => r.leaf ? r.id : r.id + ".");
					})
					.catch(() => { this.pathSuggestions = []; });
			},
			browseMetrics()
			{
				ModalDialog(GraphiteMetricBrowser, {
					initialPath: this.base,
					templateMode: this.templateMode,
					serverNames: this.serverNames
				}).then(result =>
				{
					if (result && result.path)
					{
						this.base = result.path;
						this.syncOut();
					}
				});
			},
			substitutePlaceholder()
			{
				let segments = this.base.split(".");
				for (let i = 0; i < segments.length; i++)
					for (let sn of this.serverNames)
						if (sn && segments[i].toLowerCase() === sn.toLowerCase())
							segments[i] = "%%ServerName%%";
				this.base = segments.join(".");
				this.syncOut();
			},
			//////////////////////////////////////////
			// Function chain editing               //
			//////////////////////////////////////////
			fnDef(name)
			{
				return this.functionsMeta ? this.functionsMeta[name] : null;
			},
			fnSummary(name)
			{
				let def = this.fnDef(name);
				return def ? CleanFunctionDescription(def.description).summary : "";
			},
			fnDocs(name)
			{
				let def = this.fnDef(name);
				return def ? CleanFunctionDescription(def.description).full : "";
			},
			paramInputId(fnIndex, paramIndex)
			{
				return "graphiteFnParam_" + this.uid + "_" + fnIndex + "_" + paramIndex;
			},
			/** Parameter definitions for the arguments editable in the GUI (the leading series parameter is supplied by the chain). */
			chainParamDefs(name)
			{
				let def = this.fnDef(name);
				if (!def || !def.params || !def.params.length)
					return null;
				let first = def.params[0];
				if (first.type !== "seriesList" && first.type !== "seriesLists")
					return null;
				return def.params.slice(1);
			},
			/** Builds display rows pairing parameter definitions with current argument values. */
			paramRows(fn)
			{
				let defs = this.chainParamDefs(fn.name);
				let rows = [];
				let count = defs ? Math.max(defs.length, fn.args.length) : fn.args.length;
				for (let i = 0; i < count; i++)
				{
					let def = defs && i < defs.length ? defs[i] : null;
					let arg = i < fn.args.length ? fn.args[i] : null;
					let row = {
						index: i,
						def: def,
						value: this.literalToInput(arg),
						defaultText: def && typeof def.default !== "undefined" && def.default !== null ? String(def.default) : "",
						numeric: !!(def && NUMERIC_PARAM_TYPES[def.type]),
						choices: null,
						suggestions: null
					};
					if (def && def.type === "boolean")
						row.choices = ["true", "false"];
					else if (def && def.options && def.options.length)
						row.choices = def.options.map(o => String(o));
					else if (def && def.suggestions && def.suggestions.length)
						row.suggestions = def.suggestions.map(s => String(s));
					rows.push(row);
				}
				return rows;
			},
			literalToInput(node)
			{
				if (!node)
					return "";
				if (node.t === "str")
					return node.v;
				if (node.t === "num")
					return String(node.v);
				if (node.t === "bool")
					return node.v ? "true" : "false";
				if (node.t === "none")
					return "None";
				return SerializeExpression(node);
			},
			inputToLiteral(def, value)
			{
				if (value === "" || value === null)
					return null;
				if (def && def.type === "boolean")
					return { t: "bool", v: value === "true" || value === "True" };
				if (def && NUMERIC_PARAM_TYPES[def.type] && /^-?(\d+\.?\d*|\.\d+)$/.test(value))
					return { t: "num", v: value };
				if (!def && /^-?(\d+\.?\d*|\.\d+)$/.test(value))
					return { t: "num", v: value };
				if (value === "None")
					return { t: "none" };
				return { t: "str", v: value, q: "\"" };
			},
			/** Default literal for a parameter definition, used to fill gaps before a later argument. */
			defaultLiteral(def)
			{
				if (def && typeof def.default !== "undefined" && def.default !== null)
				{
					if (def.type === "boolean")
						return { t: "bool", v: def.default === true || def.default === "true" };
					if (NUMERIC_PARAM_TYPES[def.type] && isFinite(def.default))
						return { t: "num", v: String(def.default) };
					return { t: "str", v: String(def.default), q: "\"" };
				}
				return { t: "str", v: "", q: "\"" };
			},
			setFnArg(fn, row, value)
			{
				let defs = this.chainParamDefs(fn.name);
				let literal = this.inputToLiteral(row.def, value);
				if (literal === null)
				{
					if (row.index === fn.args.length - 1)
					{
						// Clearing the last argument removes it (and any newly-trailing empty optional arguments).
						fn.args.pop();
						while (fn.args.length && defs && fn.args.length <= defs.length
							&& !defs[fn.args.length - 1].required
							&& this.literalToInput(fn.args[fn.args.length - 1]) === this.defaultOrEmpty(defs[fn.args.length - 1]))
							fn.args.pop();
					}
					else if (row.index < fn.args.length)
						fn.args[row.index] = this.defaultLiteral(row.def);
				}
				else
				{
					// Fill any earlier unset optional arguments with their defaults so positions line up.
					while (fn.args.length < row.index)
						fn.args.push(this.defaultLiteral(defs ? defs[fn.args.length] : null));
					if (row.index < fn.args.length)
						fn.args[row.index] = literal;
					else
						fn.args.push(literal);
				}
				this.syncOut();
			},
			defaultOrEmpty(def)
			{
				return def && typeof def.default !== "undefined" && def.default !== null ? String(def.default) : "";
			},
			moveFn(index, direction)
			{
				let target = index + direction;
				if (target < 0 || target >= this.fns.length)
					return;
				let item = this.fns.splice(index, 1)[0];
				this.fns.splice(target, 0, item);
				this.syncOut();
			},
			removeFn(index)
			{
				this.fns.splice(index, 1);
				this.syncOut();
			},
			toggleAddFn()
			{
				this.addFnOpen = !this.addFnOpen;
				if (this.addFnOpen)
					this.$nextTick(() => { if (this.$refs.addFnSearchInput) this.$refs.addFnSearchInput.focus(); });
			},
			addFn(def)
			{
				let fn = { name: def.name, args: [] };
				let defs = def.params.slice(1);
				for (let d of defs)
				{
					if (!d.required)
						break;
					fn.args.push(this.defaultLiteral(d));
				}
				this.fns.push(fn);
				this.addFnOpen = false;
				this.addFnSearch = "";
				this.syncOut();
			},
			//////////////////////////////////////////
			// Raw text mode                        //
			//////////////////////////////////////////
			onRawInput()
			{
				if (this.rawParseTimeout)
					clearTimeout(this.rawParseTimeout);
				this.rawParseTimeout = setTimeout(() => { this.parseRaw(); }, 300);
			},
			parseRaw()
			{
				let text = this.rawText.trim();
				this.target.raw = text;
				try
				{
					this.target.ast = text ? ParseExpression(text) : null;
					this.target.parseError = null;
					this.rawError = null;
				}
				catch (ex)
				{
					this.target.ast = null;
					this.target.parseError = ex.message;
					this.rawError = ex.message;
				}
			},
			switchToRaw()
			{
				this.rawText = this.target.raw;
				this.rawError = this.target.parseError;
				this.visualUnavailableReason = null;
				this.mode = "raw";
			},
			switchToVisual()
			{
				this.parseRaw();
				if (this.rawError)
				{
					this.visualUnavailableReason = "Fix the expression error first.";
					return;
				}
				let chain = this.target.ast ? TryBuildChain(this.target.ast) : { base: "", fns: [] };
				if (!chain)
				{
					this.visualUnavailableReason = "This expression is too complex for the visual editor (for example, a function whose arguments include another series expression). It can still be edited as text.";
					return;
				}
				this.base = chain.base;
				this.fns = chain.fns;
				this.visualUnavailableReason = null;
				this.mode = "visual";
			}
		}
	}
</script>

<style scoped>
	.targetEditorRoot
	{
		font-size: 13px;
	}

	.fieldBlock
	{
		margin-bottom: 10px;
	}

	.fieldLabel
	{
		font-weight: bold;
		margin-bottom: 3px;
	}

	.labelNote
	{
		font-weight: normal;
		color: #666666;
		font-size: 12px;
	}

	.fieldHelp
	{
		font-size: 12px;
		color: #555555;
		margin-top: 3px;
	}

	.pathRow
	{
		display: flex;
		gap: 6px;
	}

	.pathInput
	{
		flex: 1 1 auto;
		font-family: Consolas, monospace;
		font-size: 13px;
		padding: 3px 5px;
	}

	.templateSuggestion
	{
		margin-top: 4px;
	}

	.fnBox
	{
		border: 1px solid #C5C5C5;
		border-radius: 4px;
		background-color: #F7F9FB;
		padding: 6px 8px;
		margin-bottom: 6px;
	}

	.fnHeader
	{
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.fnName
	{
		font-family: Consolas, monospace;
		font-weight: bold;
		font-size: 14px;
	}

	.fnSignature
	{
		font-family: Consolas, monospace;
		font-size: 11px;
		color: #666666;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		flex: 1 1 auto;
	}

	.fnHeaderButtons
	{
		flex: 0 0 auto;
		margin-left: auto;
	}

		.fnHeaderButtons input[type=button]
		{
			margin-left: 3px;
		}

	.fnSummary
	{
		font-size: 12px;
		color: #555555;
		margin: 2px 0px 4px 0px;
	}

	.paramRow
	{
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 3px 0px;
	}

	.paramLabel
	{
		flex: 0 0 auto;
		min-width: 110px;
		font-family: Consolas, monospace;
		font-size: 12px;
	}

	.requiredMark
	{
		color: #CC0000;
	}

	.paramRow input[type=text], .paramRow input[type=number]
	{
		flex: 1 1 auto;
		font-size: 13px;
		padding: 2px 4px;
	}

	.fnDocs
	{
		margin-top: 4px;
		font-size: 12px;
	}

		.fnDocs summary
		{
			cursor: pointer;
			color: #1e78af;
		}

		.fnDocs pre
		{
			white-space: pre-wrap;
			background-color: #FFFFFF;
			border: 1px solid #DDDDDD;
			padding: 6px;
			max-height: 300px;
			overflow: auto;
		}

	.addFnRow
	{
		margin-top: 4px;
	}

	.addFnPanel
	{
		border: 1px solid #C5C5C5;
		border-radius: 4px;
		margin-top: 4px;
		padding: 6px 8px;
		max-height: 320px;
		overflow: auto;
		background-color: #FFFFFF;
	}

	.addFnSearch
	{
		width: 100%;
		box-sizing: border-box;
		margin-bottom: 6px;
		padding: 3px 5px;
		font-size: 13px;
	}

	.fnGroupName
	{
		font-weight: bold;
		margin: 6px 0px 2px 0px;
		border-bottom: 1px solid #E0E0E0;
	}

	.fnChoice
	{
		margin: 2px 0px;
		display: flex;
		gap: 8px;
		align-items: baseline;
	}

	.fnChoiceName
	{
		font-family: Consolas, monospace;
		color: #1e78af;
		cursor: pointer;
		flex: 0 0 auto;
	}

		.fnChoiceName:hover
		{
			text-decoration: underline;
		}

	.fnChoiceSummary
	{
		font-size: 11px;
		color: #666666;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.modeRow
	{
		margin-top: 6px;
	}

	.modeLink
	{
		color: #1e78af;
		cursor: pointer;
		font-size: 12px;
		text-decoration: underline;
	}

	.rawTextarea
	{
		width: 100%;
		box-sizing: border-box;
		font-family: Consolas, monospace;
		font-size: 13px;
	}

	.error
	{
		color: #FF0000;
		font-weight: bold;
		font-size: 12px;
	}
</style>
